import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useI18n } from "../i18n";
import { ArrowRight, Building2, Users, Coins, Layers } from "lucide-react";
import SEO from "../components/SEO";

const HERO_IMG = "https://images.pexels.com/photos/28752153/pexels-photo-28752153.jpeg";
const MANIFESTO_IMG = "https://images.pexels.com/photos/36373012/pexels-photo-36373012.jpeg";
const ARTISAN_IMG = "https://images.pexels.com/photos/29487649/pexels-photo-29487649.jpeg";

function Overline({ children, dark }) {
  return (
    <span className="overline" style={{ color: dark ? "rgba(247,245,240,0.65)" : "var(--terracotta)" }}>
      {children}
    </span>
  );
}

function StatCard({ value, label, unit }) {
  return (
    <div className="reveal" data-testid={`stat-${label}`}>
      <div className="font-mono text-xs" style={{ opacity: 0.6 }}>{unit}</div>
      <div className="serif-display" style={{ fontSize: 68, lineHeight: 1, marginTop: 4 }}>
        {value}
      </div>
      <div className="mt-3 text-sm" style={{ opacity: 0.75 }}>{label}</div>
    </div>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  const [portfolio, setPortfolio] = useState([]);
  const [insights, setInsights] = useState([]);
  const [stats, setStats] = useState({ companies: 0, employees: 0, aggregate_revenue_eur_m: 0, sectors: 0 });
  const [sectorFilter, setSectorFilter] = useState("all");

  // Contact form
  const [form, setForm] = useState({
    full_name: "", email: "", company: "", role: "", phone: "",
    region: "", sector: "", revenue_range: "", message: "",
    inquiry_type: "succession",
  });
  const [formStatus, setFormStatus] = useState(null);

  useEffect(() => {
    api.get("/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
    api.get("/insights").then((r) => setInsights(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const sectors = ["all", ...Array.from(new Set(portfolio.map((c) => c.sector)))];
  const filteredPortfolio = sectorFilter === "all" ? portfolio : portfolio.filter((c) => c.sector === sectorFilter);

  const submitForm = async (e) => {
    e.preventDefault();
    setFormStatus("loading");
    try {
      await api.post("/inquiries", form);
      setFormStatus("sent");
      setForm({
        full_name: "", email: "", company: "", role: "", phone: "",
        region: "", sector: "", revenue_range: "", message: "",
        inquiry_type: "succession",
      });
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <main>
      <SEO
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "name": "GIANO — Holding permanente per le PMI italiane",
              "description": "Compriamo aziende italiane. Per sempre. Holding permanente specializzata nell'acquisizione di PMI italiane senza successione.",
              "inLanguage": "it-IT",
              "isPartOf": {"@id": "https://gianoholding.com/#website"},
              "about": {"@id": "https://gianoholding.com/#organization"}
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Cos'è GIANO Holding?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "GIANO Holding è una holding permanente italiana specializzata nell'acquisizione di PMI (piccole e medie imprese) italiane senza successione. Acquistiamo aziende con ricavi tra €5M e €50M ed EBITDA superiore a €1M in nicchie difendibili, e le manteniamo nel gruppo permanentemente — nessun exit, nessuna rivendita futura."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Quanto vale la mia azienda?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Il valore di una PMI italiana dipende dal settore (multipli EBITDA 3.0x-8.5x), dalla marginalità normalizzata, dalla concentrazione clienti, dalla dipendenza dal titolare. Il nostro motore Quanto Vale calcola un range in tre scenari (conservativo, base, potenziale) in 10 minuti a partire dai bilanci degli ultimi 3 anni: gianoholding.com/valutazione."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Come funziona la cessione con GIANO?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Il processo è confidenziale: un incontro esplorativo, NDA reciproco, offerta chiara entro 30 giorni. Nessuna banca d'affari, nessun broker. Preserviamo brand, sede e team dell'azienda. In caso di sì, closing tipicamente in 90-120 giorni contro 380 giorni della media di mercato."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Chi vende un'azienda a GIANO?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Imprenditori italiani sopra i 60 anni che vogliono una successione senza vendere all'estero, senza affidarsi a un private equity con exit a 5 anni, e senza far chiudere l'azienda. GIANO è pensato per chi cerca capitale paziente e continuità, non per chi cerca il prezzo massimo assoluto."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Che settori acquista GIANO?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Meccanica, alimentare, chimica, farmaceutico, tessile di nicchia, packaging, componentistica automotive, software B2B verticali. Priorità a nicchie difendibili con vantaggio competitivo strutturale, brand storico e almeno 10 anni di operatività."
                  }
                }
              ]
            }
          ]
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: "var(--bone)" }} data-testid="hero-section">
        <div className="container-x pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7 reveal">
              <Overline>{t("hero.overline")}</Overline>
              <h1 className="serif-display mt-6" style={{ fontSize: "clamp(48px, 7vw, 96px)" }}>
                {t("hero.title_1")}<br />
                <span className="italic-accent">{t("hero.title_2")}</span><br />
                {t("hero.title_3")}
              </h1>
              <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 560, opacity: 0.82 }}>
                {t("hero.lead")}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a href="#contact" className="btn-primary" data-testid="hero-cta-sell">
                  {t("cta.sell")} <ArrowRight size={16} />
                </a>
                <a href="#approach" className="btn-ghost" data-testid="hero-cta-model">
                  {t("cta.discover")}
                </a>
              </div>
            </div>
            <div className="md:col-span-5 relative reveal reveal-delay-2">
              <div style={{
                position: "relative", aspectRatio: "4/5",
                border: "1px solid var(--border)", overflow: "hidden",
              }}>
                <img src={HERO_IMG} alt="Italian manufacturing" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "saturate(0.85)" }} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 55%, rgba(15,23,32,0.55) 100%)" }} />
                <div style={{ position: "absolute", bottom: 20, left: 20, right: 20, color: "var(--bone)" }}>
                  <div className="overline" style={{ opacity: 0.75 }}>Est. Milano</div>
                  <div className="serif-display" style={{ fontSize: 22, marginTop: 4 }}>Capitale paziente, radici italiane.</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-24 pt-10 grid grid-cols-2 md:grid-cols-4 gap-8"
               style={{ borderTop: "1px solid var(--border)" }}>
            <StatCard value={stats.companies} label={t("stats.companies")} unit="N°" />
            <StatCard value={stats.employees.toLocaleString(lang === "it" ? "it-IT" : "en-US")} label={t("stats.employees")} unit="HC" />
            <StatCard value={`€${stats.aggregate_revenue_eur_m.toFixed(0)}M`} label={t("stats.revenue")} unit="EUR" />
            <StatCard value={stats.sectors} label={t("stats.sectors")} unit="IND" />
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="relative py-24 md:py-32" style={{ background: "var(--midnight)", color: "var(--bone)" }}>
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <Overline dark>{t("manifesto.overline")}</Overline>
              <h2 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5vw, 68px)" }}>
                {t("manifesto.title_1")}<br />
                {t("manifesto.title_2")}<span style={{ color: "var(--gold)", fontStyle: "italic" }}>{t("manifesto.title_2_it")}</span>{t("manifesto.title_3")}
              </h2>
              <div className="mt-10 relative" style={{ aspectRatio: "4/5", border: "1px solid rgba(247,245,240,0.15)", overflow: "hidden", maxWidth: 380 }}>
                <img src={MANIFESTO_IMG} alt="Pantheon" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.9 }} />
              </div>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-6 text-lg" style={{ opacity: 0.9, lineHeight: 1.7 }}>
              <p>{t("manifesto.p1")}</p>
              <p>{t("manifesto.p2")}</p>
              <p style={{ borderLeft: "2px solid var(--gold)", paddingLeft: 20, fontStyle: "italic" }}>{t("manifesto.p3")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section id="approach" className="py-24 md:py-32" data-testid="approach-section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <Overline>{t("approach.overline")}</Overline>
              <h2 className="serif-display mt-6" style={{ fontSize: "clamp(36px, 4.5vw, 56px)" }}>{t("approach.title")}</h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 text-lg" style={{ opacity: 0.75 }}>
              {t("approach.subtitle")}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-0" style={{ borderTop: "1px solid var(--border)", borderLeft: "1px solid var(--border)" }}>
            {[1, 2, 3, 4, 5].map((n, idx) => (
              <div key={n} className={`p-8 md:p-10 ${idx < 3 ? "md:col-span-2" : "md:col-span-3"}`}
                   style={{ borderRight: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: idx === 0 ? "var(--muted)" : "var(--card)" }}>
                <div className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>0{n}</div>
                <h3 className="serif-display mt-4" style={{ fontSize: 28 }}>{t(`approach.p${n}.title`)}</h3>
                <p className="mt-4 text-sm" style={{ opacity: 0.75, lineHeight: 1.7 }}>{t(`approach.p${n}.body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="portfolio" className="py-24 md:py-32" style={{ background: "var(--muted)" }} data-testid="portfolio-section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-6">
              <Overline>{t("portfolio.overline")}</Overline>
              <h2 className="serif-display mt-6" style={{ fontSize: "clamp(36px, 4.5vw, 56px)" }}>{t("portfolio.title")}</h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 text-lg self-end" style={{ opacity: 0.8 }}>
              {t("portfolio.subtitle")}
            </div>
          </div>

          {/* Sector filter */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filters">
            {sectors.map((s) => (
              <button
                key={s}
                onClick={() => setSectorFilter(s)}
                data-testid={`filter-${s}`}
                className="chip"
                style={{
                  background: sectorFilter === s ? "var(--midnight)" : "transparent",
                  color: sectorFilter === s ? "var(--bone)" : "var(--midnight)",
                  borderColor: sectorFilter === s ? "var(--midnight)" : "var(--border)",
                  cursor: "pointer",
                }}
              >
                {s === "all" ? t("portfolio.all") : s}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portfolio-grid">
            {filteredPortfolio.map((c) => (
              <article key={c.id} className="card-flat" data-testid={`portfolio-card-${c.id}`}>
                <div style={{ aspectRatio: "4/3", overflow: "hidden", borderBottom: "1px solid var(--border)" }}>
                  <img src={c.image_url || ARTISAN_IMG} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(0.15) saturate(0.9)" }} />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <span className="chip">{c.sector}</span>
                    <span className="font-mono text-xs" style={{ opacity: 0.6 }}>{c.year_acquired}</span>
                  </div>
                  <h3 className="serif-display mt-4" style={{ fontSize: 26 }}>{c.name}</h3>
                  <p className="mt-3 text-sm" style={{ opacity: 0.78, lineHeight: 1.65 }}>
                    {lang === "it" ? c.description_it : c.description_en}
                  </p>
                  <div className="mt-6 pt-4 grid grid-cols-3 gap-2 font-mono text-xs" style={{ borderTop: "1px solid var(--border)" }}>
                    <div>
                      <div style={{ opacity: 0.5 }}>{t("portfolio.region")}</div>
                      <div className="mt-1" style={{ fontSize: 12 }}>{c.region}</div>
                    </div>
                    <div>
                      <div style={{ opacity: 0.5 }}>{t("portfolio.revenue")}</div>
                      <div className="mt-1">€{c.revenue_eur_m.toFixed(1)}M</div>
                    </div>
                    <div>
                      <div style={{ opacity: 0.5 }}>{t("portfolio.employees")}</div>
                      <div className="mt-1">{c.employees}</div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CRITERIA */}
      <section id="criteria" className="py-24 md:py-32" data-testid="criteria-section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <Overline>{t("criteria.overline")}</Overline>
              <h2 className="serif-display mt-6" style={{ fontSize: "clamp(36px, 4.5vw, 56px)" }}>{t("criteria.title")}</h2>
              <p className="mt-6 text-lg" style={{ opacity: 0.75 }}>{t("criteria.subtitle")}</p>
              <div className="mt-10 relative" style={{ aspectRatio: "3/4", border: "1px solid var(--border)", overflow: "hidden", maxWidth: 340 }}>
                <img src={ARTISAN_IMG} alt="Craftsmanship" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            </div>

            <div className="md:col-span-6 md:col-start-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0" style={{ borderTop: "1px solid var(--border)", borderLeft: "1px solid var(--border)" }}>
                {[
                  { n: 1, icon: Coins },
                  { n: 2, icon: Layers },
                  { n: 3, icon: Building2 },
                  { n: 4, icon: Users },
                  { n: 5, icon: Building2 },
                  { n: 6, icon: Users },
                ].map(({ n, icon: Icon }) => (
                  <div key={n} className="p-6"
                       style={{ borderRight: "1px solid var(--border)", borderBottom: "1px solid var(--border)", background: "var(--card)" }}>
                    <div className="flex items-center justify-between">
                      <Icon size={20} style={{ color: "var(--terracotta)" }} />
                      <span className="font-mono text-xs" style={{ opacity: 0.5 }}>0{n}</span>
                    </div>
                    <h4 className="serif-display mt-4" style={{ fontSize: 22 }}>{t(`criteria.c${n}.title`)}</h4>
                    <p className="mt-2 text-sm" style={{ opacity: 0.75, lineHeight: 1.6 }}>{t(`criteria.c${n}.body`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section id="insights" className="py-24 md:py-32" style={{ background: "var(--card)" }} data-testid="insights-section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-6">
              <Overline>{t("insights.overline")}</Overline>
              <h2 className="serif-display mt-6" style={{ fontSize: "clamp(36px, 4.5vw, 56px)" }}>{t("insights.title")}</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0" style={{ borderTop: "1px solid var(--border)" }}>
            {insights.map((it, idx) => (
              <article key={it.id} className="p-8 group"
                       style={{
                         borderRight: idx < insights.length - 1 ? "1px solid var(--border)" : "none",
                         borderBottom: "1px solid var(--border)"
                       }}
                       data-testid={`insight-${it.slug}`}>
                <div className="font-mono text-xs" style={{ color: "var(--terracotta)" }}>{it.category.toUpperCase()}</div>
                <h3 className="serif-display mt-4" style={{ fontSize: 30, lineHeight: 1.15 }}>
                  {lang === "it" ? it.title_it : it.title_en}
                </h3>
                <p className="mt-4 text-sm" style={{ opacity: 0.78, lineHeight: 1.65 }}>
                  {lang === "it" ? it.excerpt_it : it.excerpt_en}
                </p>
                <Link to={`/insights/${it.slug}`}
                      data-testid={`insight-read-${it.slug}`}
                      className="mt-8 inline-flex items-center gap-2 font-mono text-xs group-hover:gap-3 transition-all"
                      style={{ color: "var(--midnight)" }}>
                  {t("insights.readmore")} <ArrowRight size={14} />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 md:py-32" style={{ background: "var(--bone)" }} data-testid="contact-section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <Overline>{t("contact.overline")}</Overline>
              <h2 className="serif-display mt-6" style={{ fontSize: "clamp(36px, 4.5vw, 56px)" }}>{t("contact.title")}</h2>
              <p className="mt-6 text-lg" style={{ opacity: 0.78, lineHeight: 1.7 }}>{t("contact.lead")}</p>
              <div className="mt-10 font-mono text-xs" style={{ opacity: 0.7 }}>
                <div>info@giano.com</div>
                <div className="mt-1">+39 02 4700 3200</div>
                <div className="mt-1">Via Broletto 16 · 20121 Milano</div>
              </div>
            </div>

            <form onSubmit={submitForm} className="md:col-span-6 md:col-start-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="contact-form">
              <div>
                <label className="overline">{t("contact.form.name")} *</label>
                <input required data-testid="form-name" className="input-line" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div>
                <label className="overline">{t("contact.form.email")} *</label>
                <input required type="email" data-testid="form-email" className="input-line" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="overline">{t("contact.form.company")} *</label>
                <input required data-testid="form-company" className="input-line" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div>
                <label className="overline">{t("contact.form.role")}</label>
                <input data-testid="form-role" className="input-line" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
              </div>
              <div>
                <label className="overline">{t("contact.form.phone")}</label>
                <input data-testid="form-phone" className="input-line" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="overline">{t("contact.form.region")}</label>
                <input data-testid="form-region" className="input-line" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
              </div>
              <div>
                <label className="overline">{t("contact.form.sector")}</label>
                <input data-testid="form-sector" className="input-line" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
              </div>
              <div>
                <label className="overline">{t("contact.form.revenue")}</label>
                <select data-testid="form-revenue" className="input-line" style={{ background: "transparent" }} value={form.revenue_range} onChange={(e) => setForm({ ...form, revenue_range: e.target.value })}>
                  <option value="">—</option>
                  <option value="<5">{t("contact.revenue.opt1")}</option>
                  <option value="5-15">{t("contact.revenue.opt2")}</option>
                  <option value="15-30">{t("contact.revenue.opt3")}</option>
                  <option value="30-50">{t("contact.revenue.opt4")}</option>
                  <option value=">50">{t("contact.revenue.opt5")}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="overline">{t("contact.form.message")} *</label>
                <textarea required data-testid="form-message" rows={4} className="input-line" style={{ resize: "vertical" }} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex items-center gap-6 mt-2">
                <button type="submit" disabled={formStatus === "loading"} className="btn-primary" data-testid="form-submit">
                  {formStatus === "loading" ? "..." : t("contact.form.submit")} <ArrowRight size={16} />
                </button>
                {formStatus === "sent" && (
                  <div className="font-mono text-xs" style={{ color: "#065F46" }} data-testid="form-success">
                    {t("contact.form.sent")}
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="font-mono text-xs" style={{ color: "var(--terracotta)" }} data-testid="form-error">
                    {t("contact.form.error")}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
